import { useEffect, useRef } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useDispatch } from 'react-redux';
import { Dispatch } from '../store';
import { Box, CircularProgress, Typography } from '@mui/material';
import { handleAuthCallback } from '../auth/authService';
import { setTokens } from '../auth/session';
import api from '../API';

/**
 * A sign-in that fails here used to land the user back on /login with nothing
 * on screen and only a console line, so neither they nor we could tell why.
 * Report it, and hand the reason to the login page to show.
 */
const failSignIn = (navigate: NavigateFunction, reason: string, extra: Record<string, unknown>) => {
  Sentry.captureMessage('sign-in callback failed', { level: 'warning', extra: { reason, ...extra } });
  navigate('/login', { state: { authError: reason } });
};

/**
 * Cognito's refusal when the Google account signing in is still attached to
 * another person's Dooglie account. This happens when a firm renames a leaver's
 * Google account for a new hire (Survival, 2026-10-05). Removing the leaver's
 * seat releases that login, so the message points at the admin who can fix it.
 * This matches a fixed Cognito error string, not free text.
 */
const LOGIN_HELD_BY_ANOTHER_ACCOUNT = 'User account already exists for the alias';

const AuthCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<Dispatch>();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        const url = new URL(window.location.href);
        const err = url.searchParams.get('error');
        const errDesc = url.searchParams.get('error_description');
        if (err) {
          console.error('Authentication Error:', err, errDesc);
          failSignIn(
            navigate,
            errDesc?.startsWith(LOGIN_HELD_BY_ANOTHER_ACCOUNT)
              ? "This Google account is still attached to a former colleague's Dooglie account. Ask your Dooglie admin to remove that person's seat, then sign in again."
              : `Sign-in failed: ${errDesc || err}`,
            {
              source: 'identity_provider',
              error: err,
              error_description: errDesc,
            },
          );
          return;
        }

        const hashParams = new URLSearchParams(url.hash.substring(1));
        const code = hashParams.get('code');
        const provider = hashParams.get('provider');

        if (code && provider === 'microsoft') {
          const response = await api.post('/microsoft_login_exchange', { code });
          const { id_token, refresh_token } = response.data;
          setTokens({
            idToken: id_token,
            refreshToken: refresh_token,
          });
          dispatch.auth.setUserAuthenticated(true);
          window.location.replace('/home');
          return;
        }

        const user = await handleAuthCallback();
        if (!user.id_token) {
          throw new Error('Cognito returned no id token');
        }
        setTokens({
          idToken: user.id_token,
          refreshToken: user.refresh_token,
        });
        dispatch.auth.setUserAuthenticated(true);
        window.location.replace('/home');
      } catch (e) {
        console.error('Authentication callback error', e);
        failSignIn(navigate, `Sign-in failed: ${e instanceof Error ? e.message : String(e)}`, {
          source: 'callback',
          error: String(e),
        });
      }
    })();
  }, [navigate, dispatch]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <CircularProgress />
      <Typography sx={{ mt: 2 }}>Signing you in, please wait...</Typography>
    </Box>
  );
};

export default AuthCallback;
